import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { Undo2, Redo2, HelpCircle } from "lucide-react";
import { warn } from "@/lib/log";

import { colors } from "@/theme";
import { useUndo } from "@/lib/useUndo";
import { generateReport, computeFinancials } from "@/lib/financials";
import Viewport3D from "@/components/Viewport3D";
import Dashboard from "@/components/Dashboard";
import KPIRibbon from "@/components/KPIRibbon";
import AIAssistant from "@/components/AIAssistant";
import QuickTour from "@/components/QuickTour";
import WelcomeGate from "@/components/WelcomeGate";
import EnergyProfilePanel from "@/components/EnergyProfilePanel";
import AuthWidget from "@/components/AuthWidget";
import AuthModal from "@/components/AuthModal";
import ReportDetailsModal from "@/components/ReportDetailsModal";
import ProfilePanel from "@/components/ProfilePanel";
import AdminPanel from "@/components/AdminPanel";
import { useAuth } from "@/store/useAuth";
import { ensureSession, trackEvent } from "@/api/client";
import { useStudio } from "@/store/useStudio";
import { computeKPIs, computeViability, computeStructuralLoad, effectiveCircWeights, circularityLoops } from "@/lib/kbLogic";
import { glazingAggregate } from "@/lib/glazing";
import { projectsApi } from "@/api/projects";
import { kbApi } from "@/api/kb";
import { fetchWeather, mockWeather, parseEPW } from "@/lib/weather";
import { countryFromGeo } from "@/lib/operatingProfile";
import { fileToSource, sourceToFile, downloadProject, isDsymbiaFile } from "@/lib/dsymbia";
import { saveSession, loadSession, clearSession, hasMeaningfulSession, sessionSummary } from "@/lib/sessionCache";

const RING_HEX = { locations: "#51aafe", systems: "#f88528", crops: "#71d85e", resources: "#3bbbc2" };
const AUTOSAVE_PROMPT_MS = 10 * 60 * 1000; // prompt after 10 min of active session
const AUTOSAVE_INTERVAL_MS = 15 * 60 * 1000; // silent save cadence once enabled
const AUTOSAVE_MAX_BYTES = 14 * 1024 * 1024; // stay under Mongo's 16MB doc limit

const LOC_TO_OVERLAY = {
  rooftop: "rooftop",
  ground: "ground",
  indoor: "interior",
  balcony: "facade",
  facade_s: "facade",
  facade_e: "facade",
  facade_w: "facade",
  facade_n: "facade",
};

// A/B copy for the guest→account conversion nudge (variant tracked in analytics events).
const NUDGE_COPY = {
  A: {
    title: "Design saved. Keep it forever?",
    body: "You're working as a guest, so scenarios live only in this browser. Create a free account to save and sync your scenarios across devices, so your work stays even if you switch machines.",
    cta: "Create free account",
  },
  B: {
    title: "Nice work. Let's keep it safe",
    body: "Your first design is saved on this device. Make a free account and we'll keep every scenario backed up and ready on any device you sign in from, with no lost work, ever.",
    cta: "Save my work",
  },
};


export default function App() {
  const authInit = useAuth((st) => st.init);
  const authUser = useAuth((st) => st.user);
  const openAuthModal = useAuth((st) => st.openModal);
  useEffect(() => {
    ensureSession().catch(() => {});
    authInit();
  }, [authInit]);
  const {
    kb, setKb,
    model, setModel,
    geo, setGeo,
    weather, setWeather, setWeatherLoading, setWeatherError,
    selections, areas, setArea, areaFocusSystem, targets, northOffset,
    systemBlocks,
    surfaceTags,
    systemWeightOverride,
    setProjectId, saveStatus, setSaveStatus,
    scenarios, setScenarios, loadScenario, startNewScenario,
    setSourceFile, serializeProject, rehydrateProject,
    buildingTypology, occupancy, grossAreas, setOpportunity, setTarget,
    synergyWeights, resourceInputs, carbonBaseline,
    circWeights, circLockEqual, valorizationAreaCharged,
    scope, setScope,
    rootPillar, analysisMode, toggleAnalysis,
    mepReserve, shadowPct,
  } = useStudio();

  const [modelFile, setModelFile] = useState(null);
  const [useSample, setUseSample] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState(null);
  const [showNewScenario, setShowNewScenario] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const fetchIdRef = useRef(0);
  const toastTimerRef = useRef(null);
  const prevViaRef = useRef(null);
  const suppressViaRef = useRef(false);
  const [panelWidth, setPanelWidth] = useState(500); // resizable right config rail (px)
  const resizingRef = useRef(false);
  // --- P4: project-level autosave ---
  const [autosaveOn, setAutosaveOn] = useState(() => localStorage.getItem("dsymbia_autosave") === "on");
  const [projectName, setProjectName] = useState(() => localStorage.getItem("dsymbia_project_name") || "");
  const [autosavePromptOpen, setAutosavePromptOpen] = useState(false);
  const [nameProjectOpen, setNameProjectOpen] = useState(false);
  const [projectNameDraft, setProjectNameDraft] = useState("");
  const autosavePromptedRef = useRef(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [signupNudgeOpen, setSignupNudgeOpen] = useState(false);
  const [nudgeVariant, setNudgeVariant] = useState("A");
  // --- Local session cache (crash/refresh recovery, no account) ---
  const [resumeData, setResumeData] = useState(null);     // cached session awaiting a Resume choice
  const [resumeDecided, setResumeDecided] = useState(false); // gates auto-caching until the user decides
  const startResize = useCallback((e) => {
    resizingRef.current = true;
    e.preventDefault();
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);
  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current) return;
      const w = window.innerWidth - e.clientX; // panel is docked right; drag left = wider
      const min = 340, max = Math.min(900, Math.round(window.innerWidth * 0.6));
      setPanelWidth(Math.max(min, Math.min(max, w)));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const { undo, redo, canUndo, canRedo } = useUndo(10);
  const modelReady = model.status === "ready";

  // Quick Tour — auto-launch once on a user's first visit (remembered in localStorage).
  useEffect(() => {
    try { if (!localStorage.getItem("dsymbia_tour_done")) setTourOpen(true); } catch { /* storage disabled */ }
  }, []);
  const closeTour = useCallback(() => {
    setTourOpen(false);
    try { localStorage.setItem("dsymbia_tour_done", "1"); } catch { /* storage disabled */ }
    // Finishing the tour returns the user to a blank slate: unload the sample model and collapse
    // any tabs the tour opened.
    setModelFile(null);
    setUseSample(false);
    setSourceFile(null);
    useStudio.getState().collapseSidebar();
  }, [setSourceFile]);
  const startTour = useCallback(() => setTourOpen(true), []);

  // Load the seeded backend knowledge base once on boot
  useEffect(() => {
    let alive = true;
    kbApi
      .bootstrap()
      .then((data) => { if (alive) setKb(data); })
      .catch((e) => warn("KB bootstrap failed", e));
    return () => { alive = false; };
  }, [setKb]);

  // On boot, detect a locally-cached session and offer to resume it (see the Resume banner).
  useEffect(() => {
    const cached = loadSession();
    if (cached && hasMeaningfulSession(cached)) setResumeData(cached);
    else setResumeDecided(true); // nothing to resume → start caching immediately
  }, []);

  // Restore the cached design into the studio. Geometry isn't cached, so the model must be
  // re-uploaded; all design data (site, selections, systems, allocations, energy, suppliers) returns.
  const resumeLastSession = useCallback(() => {
    if (!resumeData) return;
    rehydrateProject(resumeData);
    setModelFile(null);
    setUseSample(false);
    setResumeData(null);
    setResumeDecided(true);
    const hadModel = !!resumeData.state?.systemBlocks?.length;
    showToast(hadModel
      ? "Session restored. Re-upload your 3D model to see the volumes again."
      : "Session restored.");
  }, [resumeData, rehydrateProject, showToast]);

  // Discard the cache and begin a clean session (which then starts caching afresh).
  const dismissResume = useCallback(() => {
    clearSession();
    setResumeData(null);
    setResumeDecided(true);
  }, []);

  // Auto-cache the working session to localStorage (debounced) on every change, once the
  // user has decided about any prior session. This is the crash/refresh safety net.
  useEffect(() => {
    if (!resumeDecided) return;
    let t;
    const unsub = useStudio.subscribe(() => {
      clearTimeout(t);
      t = setTimeout(() => {
        try {
          const project = serializeProject();
          if (hasMeaningfulSession(project)) saveSession(project);
        } catch (e) { warn("session cache failed", e); }
      }, 700);
    });
    return () => { clearTimeout(t); unsub(); };
  }, [resumeDecided, serializeProject]);

  // Load saved scenarios for comparison cards
  const loadScenarios = useCallback(async () => {
    try {
      const list = await projectsApi.list();
      setScenarios(list);
    } catch (e) {
      warn("scenarios load failed", e);
    }
  }, [setScenarios]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  // Invite deep link (?join=<code>): auto-join + open the shared project once signed in.
  const [pendingJoin, setPendingJoin] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("join") || ""; } catch { return ""; }
  });
  useEffect(() => {
    if (!pendingJoin) return;
    if (!authUser) { openAuthModal("login"); showToast("Sign in to join this shared session"); return; }
    (async () => {
      try {
        const r = await projectsApi.join(pendingJoin);
        const p = await projectsApi.get(r.id);
        loadScenario(p);
        showToast(`Joined “${r.name || "shared project"}” — you can now build on it together`);
        await loadScenarios();
      } catch { showToast("Invalid or expired invite link"); }
      setPendingJoin("");
      try { window.history.replaceState({}, "", window.location.pathname); } catch { /* noop */ }
    })();
  }, [pendingJoin, authUser, openAuthModal, showToast, loadScenario, loadScenarios]);

  // Presence heartbeat — marks the current user active on the open project so collaborators
  // (sequential handoff) can see who's in the session via the Share panel.
  const activeProjectId = useStudio((st) => st.projectId);
  useEffect(() => {
    if (!authUser || !activeProjectId) return;
    const beat = () => projectsApi.presence(activeProjectId).catch(() => { /* noop */ });
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [authUser, activeProjectId]);

  const effCircWeights = useMemo(() => effectiveCircWeights(circWeights, circLockEqual), [circWeights, circLockEqual]);
  const kpis = useMemo(() => {
    const ri = { ...resourceInputs, countryName: countryFromGeo(geo) };
    const glazing = glazingAggregate(surfaceTags, geo?.lat);
    const base = computeKPIs(kb, selections, areas, weather, systemBlocks, occupancy, buildingTypology, ri, synergyWeights, carbonBaseline, effCircWeights, valorizationAreaCharged, glazing);
    const load = computeStructuralLoad(kb, systemBlocks, systemWeightOverride);
    return { ...base, structural_load_kg: load.totalKg, structural_load_rows: load.rows };
  }, [kb, selections, areas, weather, systemBlocks, systemWeightOverride, occupancy, buildingTypology, resourceInputs, synergyWeights, carbonBaseline, geo, effCircWeights, valorizationAreaCharged, surfaceTags]);
  const viability = useMemo(() => computeViability(kb, selections, areas, weather, kpis, effCircWeights), [kb, selections, areas, weather, kpis, effCircWeights]);

  const handleGenerateReport = useCallback(() => {
    const s = useStudio.getState();
    const financials = computeFinancials(s.systemBlocks, s.kb, s.geo?.name, s.financialOverrides);
    let projectImage = null;
    try { projectImage = reportCaptureRef.current?.(); } catch (e) { projectImage = null; }
    generateReport({ geo: s.geo, kpis, blocks: s.systemBlocks, kb: s.kb, financials, opportunity: s.opportunity, overrides: s.financialOverrides, suppliers: s.pickedSuppliers, projectImage, surfaceTags: s.surfaceTags, weather: s.weather, branding: s.reportMeta, occupancy: s.occupancy, buildingTypology: s.buildingTypology, scope: s.scope, contextShade: s.contextShade, zoneAllocations: s.zoneAllocations, resourceInputs: s.resourceInputs, selections: s.selections });
    showToast("Opening PDF report…");
  }, [kpis, showToast]);

  // CEA (Controlled Environment Agriculture) is active when an indoor/controlled surface or system
  // is chosen — this unlocks the building's interior surfaces as growing opportunities.
  const ceaActive = useMemo(() => {
    if ((selections.locations || []).includes("indoor")) return true;
    const sysGroup = new Map((kb?.systems || []).map((x) => [x.id, x.group]));
    return (selections.systems || []).some((id) => (sysGroup.get(id) || "").toLowerCase().includes("controlled"))
      || (systemBlocks || []).some((b) => b.locationId === "indoor");
  }, [selections.locations, selections.systems, systemBlocks, kb]);

  // Section 5: recompute the Dynamic Opportunity Area (backend) whenever the building gross
  // areas, typology, occupancy or site change; place the Location ring's default target notch.
  const lastNotchRef = useRef(null);
  useEffect(() => {
    if (!grossAreas) return; // snap the Location notch the moment geometry is ingested
    let cancelled = false;
    kbApi.opportunity({
      gross: grossAreas, typology: buildingTypology || "residential", occupancy: occupancy || null,
      lat: geo?.lat ?? null, mep_reserve: mepReserve || 0, shadow_pct: shadowPct || 0,
      dli: weather?.dli ?? null, ghi: weather?.ghi ?? null, cea: ceaActive,
    })
      .then((res) => {
        if (cancelled) return;
        setOpportunity(res);
        const cur = useStudio.getState().targets.locations;
        if (cur == null || cur === lastNotchRef.current) {
          setTarget("locations", res.notch_pct);
          lastNotchRef.current = res.notch_pct;
        }
      })
      .catch((e) => warn("opportunity calc failed", e));
    return () => { cancelled = true; };
  }, [grossAreas, buildingTypology, occupancy, geo, mepReserve, shadowPct, weather, ceaActive, setOpportunity, setTarget]);

  // One-time notice when CEA turns on and there is interior area to unlock.
  const ceaNotifiedRef = useRef(false);
  const reportCaptureRef = useRef(null); // Viewport3D assigns a () => pngDataUrl snapshot fn here
  useEffect(() => {
    const interior = grossAreas?.interior || 0;
    if (ceaActive && interior > 0 && !ceaNotifiedRef.current) {
      ceaNotifiedRef.current = true;
      showToast(`Controlled Environment Agriculture selected. ${Math.round(interior).toLocaleString()} m² of interior surfaces are now counted as growing opportunities.`);
    }
    if (!ceaActive) ceaNotifiedRef.current = false;
  }, [ceaActive, grossAreas, showToast]);

  // Dynamic guidance: when a ring's viability shifts from an action, coach the user on *why*
  useEffect(() => {
    const cur = viability.scores;
    const prev = prevViaRef.current;
    prevViaRef.current = cur;
    if (!prev) return; // skip initial mount
    if (suppressViaRef.current) { suppressViaRef.current = false; return; } // skip right after a restore
    const keys = ["locations", "systems", "crops", "resources"];
    let best = null;
    let bestDelta = 0;
    keys.forEach((k) => {
      const d = Math.abs((cur[k] || 0) - (prev[k] || 0));
      if (d > bestDelta) { bestDelta = d; best = k; }
    });
    if (!best || bestDelta < 0.03) return;
    const label = { locations: "Zone", systems: "Systems", crops: "Crops", resources: "Resources" }[best];
    if ((cur[best] || 0) >= 0.999) showToast(`✓ ${label} viability fully resolved`);
    else if (viability.hints[best]) showToast(viability.hints[best]);
  }, [viability, showToast]);
  const highlights = useMemo(
    () => [...new Set(selections.locations.map((l) => LOC_TO_OVERLAY[l]).filter(Boolean))],
    [selections.locations]
  );
  const expandedRibbon = typeof window !== "undefined" ? Math.max(360, window.innerHeight * 0.6) : 520;

  const applyLocation = useCallback(
    async ({ lat, lng, name }) => {
      const displayName = name || `${lat.toFixed(2)}°, ${lng.toFixed(2)}°`;
      setGeo({ lat, lng, name: displayName });

      // .epw override is sticky — never overwritten by a location change
      if (useStudio.getState().weather?.source === "epw") return;

      const id = ++fetchIdRef.current;
      setWeatherLoading(true);
      setWeatherError(null);
      try {
        const w = await fetchWeather({ lat, lng });
        if (id === fetchIdRef.current) setWeather(w);
      } catch {
        if (id === fetchIdRef.current) {
          setWeather(mockWeather({ lat, lng }));
          setWeatherError("Live weather unavailable; showing estimate.");
        }
      } finally {
        if (id === fetchIdRef.current) setWeatherLoading(false);
      }
    },
    [setGeo, setWeather, setWeatherLoading, setWeatherError]
  );

  const handleEpwFile = useCallback(
    async (file) => {
      try {
        const parsed = parseEPW(await file.text());
        setWeather(parsed); // climate only — geo & geometry untouched
      } catch (e) {
        warn("epw parse failed", e);
        setWeatherError("Could not parse that .epw file.");
      }
    },
    [setWeather, setWeatherError]
  );

  // Clear an .epw override and revert to live climate for the pinned site.
  const handleEpwClear = useCallback(async () => {
    setWeatherError(null);
    const g = useStudio.getState().geo;
    if (!g?.lat) { setWeather(null); return; }
    const id = ++fetchIdRef.current;
    setWeatherLoading(true);
    try {
      const w = await fetchWeather({ lat: g.lat, lng: g.lng });
      if (id === fetchIdRef.current) setWeather(w);
    } catch {
      if (id === fetchIdRef.current) {
        setWeather(mockWeather({ lat: g.lat, lng: g.lng }));
        setWeatherError("Live weather unavailable; showing estimate.");
      }
    } finally {
      if (id === fetchIdRef.current) setWeatherLoading(false);
    }
  }, [setWeather, setWeatherError, setWeatherLoading]);

  const onPickModel = useCallback((file) => {
    setUseSample(false);
    setModelFile(file);
    trackEvent("model_upload", { name: file?.name, size: file?.size });
    // Capture the original geometry (Base64) for .dsymbia export
    fileToSource(file).then(setSourceFile).catch((e) => warn("source capture failed", e));
  }, [setSourceFile]);
  const onUseSample = useCallback(() => {
    setModelFile(null);
    setUseSample(true);
    setSourceFile(null); // sample building has no source file
  }, [setSourceFile]);
  const onClearModel = useCallback(() => {
    setModelFile(null);
    setUseSample(false);
    setSourceFile(null);
  }, [setSourceFile]);

  // --- P4: whole-project autosave ---
  const runAutosave = useCallback(async (name) => {
    const pname = (name || projectName || "").trim();
    if (!pname) return false;
    try {
      const project = serializeProject();
      project.projectName = pname;
      let payload = project;
      // Guard against Mongo's 16MB doc limit — drop the heavy base64 model if the bundle is too big.
      if (JSON.stringify(project).length > AUTOSAVE_MAX_BYTES) {
        payload = { ...project, model: null, model_omitted: true };
      }
      const res = await projectsApi.autosave(pname, payload);
      showToast(payload.model_omitted ? "Autosaved (state only; model too large)" : "Autosaved ✓");
      return res;
    } catch (e) {
      warn("autosave failed", e);
      return false;
    }
  }, [projectName, serializeProject, showToast]);

  const enableAutosave = useCallback((name) => {
    setAutosaveOn(true);
    localStorage.setItem("dsymbia_autosave", "on");
    setAutosavePromptOpen(false);
    runAutosave(name); // immediate first snapshot
  }, [runAutosave]);

  const confirmProjectName = useCallback(() => {
    const n = projectNameDraft.trim();
    if (!n) return;
    setProjectName(n);
    localStorage.setItem("dsymbia_project_name", n);
    setNameProjectOpen(false);
    enableAutosave(n);
  }, [projectNameDraft, enableAutosave]);

  const acceptAutosavePrompt = useCallback(() => {
    if (projectName) enableAutosave(projectName);
    else { setProjectNameDraft(""); setAutosavePromptOpen(false); setNameProjectOpen(true); }
  }, [projectName, enableAutosave]);

  // Prompt once, after 10 min of active session, if autosave isn't already on.
  useEffect(() => {
    if (autosaveOn || autosavePromptedRef.current) return;
    const t = setTimeout(() => {
      autosavePromptedRef.current = true;
      setAutosavePromptOpen(true);
    }, AUTOSAVE_PROMPT_MS);
    return () => clearTimeout(t);
  }, [autosaveOn]);

  // Silent 15-min autosave loop once enabled and a project is defined.
  useEffect(() => {
    if (!autosaveOn || !projectName) return;
    const iv = setInterval(() => { if (modelReady) runAutosave(); }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [autosaveOn, projectName, modelReady, runAutosave]);

  // Export the entire studio (state + Base64 geometry) to a downloadable [name].dsymbia file
  const exportProject = useCallback((includeScenarios = false) => {
    try {
      const project = serializeProject();
      if (includeScenarios) {
        project.scenarios = scenarios || [];
        project.comparison = { count: (scenarios || []).length, generatedAt: new Date().toISOString() };
      }
      downloadProject(project);
      trackEvent("data_export", { include_scenarios: includeScenarios, scenario_count: (scenarios || []).length });
      showToast(includeScenarios
        ? `Project exported with ${(scenarios || []).length} scenario${(scenarios || []).length === 1 ? "" : "s"}`
        : "Project exported (current design only)");
    } catch (e) {
      warn("export failed", e);
      showToast("Export failed; model may be too large for this browser");
    }
    setExportModalOpen(false);
  }, [serializeProject, showToast, scenarios]);

  // Open an existing .dsymbia file: rehydrate state, then re-ingest the bundled geometry
  const importProject = useCallback(async (file) => {
    try {
      const data = JSON.parse(await file.text());
      if (data.format !== "rcengine" && data.format !== "dsymbia") { showToast("That doesn't look like a .rcengine project file"); return; }
      rehydrateProject(data);
      trackEvent("data_import", { name: data.projectName });
      const f = sourceToFile(data.model);
      if (f) { setUseSample(false); setModelFile(f); }
      else { setModelFile(null); setUseSample(true); } // project bundled the sample building
      suppressViaRef.current = true;
      showToast(`Opened “${data.projectName || "project"}” — resuming your scenario`);
    } catch (e) {
      warn("import failed", e);
      showToast("Could not open that .rcengine file");
    }
  }, [rehydrateProject, showToast]);

  // Surface pick from the 3D model fills the focused system's area (m²)
  const onSurfaceArea = useCallback((area) => {
    const sys = useStudio.getState().areaFocusSystem;
    if (sys) setArea(sys, area);
  }, [setArea]);

  // Bi-directional: a surface click in the 3D viewport tagged a location
  const onSurfaceTag = useCallback(({ tag }) => {
    showToast(`${tag} selected. Systems filtered to compatible options`);
  }, [showToast]);

  const saveProject = useCallback(async () => {
    setSaveStatus("saving");
    try {
      const body = {
        name: `${geo?.name || "Site"} · ${selections.systems.length} sys · ${Math.round(kpis.total_area)}m²`,
        onboarding: {
          environment: "both",
          location: geo?.name || null,
          dli: weather?.dli ?? null,
          temperature: weather?.temperature ?? null,
          humidity: weather?.humidity ?? null,
        },
        selections: {
          locations: selections.locations,
          systems: selections.systems,
          crops: selections.crops,
          resources: selections.resources,
          location: selections.locations[0] || null,
          system: selections.systems[0] || null,
        },
        kpis: {
          water_recovery: kpis.water_recovery,
          total_yield: kpis.total_yield,
          energy_offset: kpis.energy_offset,
          system_health: kpis.system_health,
          total_area: kpis.total_area,
        },
        areas,
        targets,
        north_offset: northOffset,
        geo,
        weather,
        system_blocks: systemBlocks,
        circ_weights: effCircWeights,
        circ_lock_equal: circLockEqual,
        circ_loops: kpis?.balance
          ? Object.fromEntries(circularityLoops(kpis.balance, effCircWeights).map((r) => [r.key, r.pct]))
          : {},
      };
      const created = await projectsApi.create(body);
      trackEvent("scenario_save", { name: body?.project_name || body?.name, location: geo?.label });
      setProjectId(created.id);
      await loadScenarios();
      setSaveStatus("saved");
      // Guest conversion nudge — offer account sync once, right after the first saved design.
      const isGuest = !useAuth.getState().user;
      let alreadyNudged = false;
      try { alreadyNudged = localStorage.getItem("dsymbia_signup_nudged") === "1"; } catch { /* ignore */ }
      if (isGuest && !alreadyNudged) {
        try { localStorage.setItem("dsymbia_signup_nudged", "1"); } catch { /* ignore */ }
        let variant;
        try { variant = localStorage.getItem("dsymbia_nudge_variant"); } catch { /* ignore */ }
        if (variant !== "A" && variant !== "B") {
          variant = Math.random() < 0.5 ? "A" : "B";
          try { localStorage.setItem("dsymbia_nudge_variant", variant); } catch { /* ignore */ }
        }
        setNudgeVariant(variant);
        setSignupNudgeOpen(true);
        trackEvent("signup_nudge_shown", { variant });
      } else {
        setShowNewScenario(true); // §12 — offer the slate-wipe / continue choice
      }
      setTimeout(() => setSaveStatus("idle"), 2400);
    } catch (e) {
      warn("save failed", e);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  }, [selections, geo, weather, kpis, areas, targets, northOffset, systemBlocks, setProjectId, setSaveStatus, loadScenarios, effCircWeights, circLockEqual]);

  const deleteScenario = useCallback(async (id) => {
    try {
      await projectsApi.remove(id);
      await loadScenarios();
    } catch (e) {
      warn("delete scenario failed", e);
    }
  }, [loadScenarios]);

  const handleLoadScenario = useCallback((sc) => {
    suppressViaRef.current = true;
    loadScenario(sc);
    setExpanded(false);
    showToast(`Restored “${sc.name}” into the studio`);
  }, [loadScenario, showToast]);

  const renameScenario = useCallback(async (id, name) => {
    try {
      await projectsApi.update(id, { name });
      await loadScenarios();
      showToast("Scenario renamed");
    } catch (e) {
      warn("rename scenario failed", e);
      showToast("Rename failed. Please try again");
    }
  }, [loadScenarios, showToast]);

  // Site & Climate is now a sticky card at the top of the sidebar (no blocking gate)
  return (
    <div style={styles.root}>
      <div style={styles.topRow}>
        {/* Viewport LEFT 70% */}
        <div style={styles.viewportCol}>
          {/* Left tool dock — ordered: Undo/Redo · Building-Neighborhood · Measure · Section · Save · PDF */}
          <div style={{ ...styles.viewToolbar, top: 16, flexDirection: "row", alignItems: "center" }} data-testid="view-toolbar">
            <div style={styles.undoGroup}>
              <button
                style={{ ...styles.iconBtn, opacity: canUndo ? 1 : 0.35, cursor: canUndo ? "pointer" : "default" }}
                onClick={undo}
                disabled={!canUndo}
                title="Undo"
                data-testid="undo-btn"
              >
                <Undo2 size={16} strokeWidth={2.4} />
              </button>
              <button
                style={{ ...styles.iconBtn, opacity: canRedo ? 1 : 0.35, cursor: canRedo ? "pointer" : "default" }}
                onClick={redo}
                disabled={!canRedo}
                title="Redo"
                data-testid="redo-btn"
              >
                <Redo2 size={16} strokeWidth={2.4} />
              </button>
            </div>
            <button
              style={styles.tourBtn}
              onClick={startTour}
              data-testid="quick-tour-launch"
              title="Quick Tour: orient yourself"
            >
              <HelpCircle size={16} strokeWidth={2.3} />
              Quick Tour
            </button>
          </div>
          {modelReady && (
            <div style={{ ...styles.viewToolbar, top: 60 }}>
              <div style={styles.scopeToggle} data-testid="scope-toggle">
                {[["building", "Building"], ["neighborhood", "Neighborhood"]].map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => { setScope(id); showToast(id === "neighborhood" ? "Neighborhood scope. All masses pool into calculations" : "Building scope. Only the primary building counts"); }}
                    style={{ ...styles.scopeBtn, ...(scope === id ? styles.scopeBtnOn : {}) }}
                    data-testid={`scope-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {modelReady && (
            <div style={{ ...styles.viewToolbar, top: 104 }}>
              <button
                onClick={toggleAnalysis}
                style={{ ...styles.possibilitiesBtn, ...(analysisMode ? { background: RING_HEX[rootPillar] || colors.brand, color: "#fff" } : {}) }}
                data-testid="analysis-toggle"
                title="Highlight viable surfaces for the active loop"
              >
                <span style={{ ...styles.possibilitiesDot, background: analysisMode ? "#fff" : (RING_HEX[rootPillar] || colors.brand) }} />
                {analysisMode ? "Analysis Mode · On" : "Show Possibilities"}
              </button>
            </div>
          )}
          <Viewport3D
            modelFile={modelFile}
            useSample={useSample}
            highlights={highlights}
            model={model}
            setModel={setModel}
            areaFocusSystem={areaFocusSystem}
            faceScores={viability?.scores}
            onSurfaceArea={onSurfaceArea}
            onPickModel={onPickModel}
            onUseSample={onUseSample}
            onClear={onClearModel}
            onImportProject={importProject}
            onSurfaceTag={onSurfaceTag}
            onNotify={showToast}
            onSaveProject={saveProject}
            onGenerateReport={handleGenerateReport}
            onReportDetails={() => setReportModalOpen(true)}
            captureRef={reportCaptureRef}
            onExportProject={() => setExportModalOpen(true)}
          />
        </div>
        {/* Resize handle — drag to widen/narrow the config rail */}
        <div
          style={styles.resizeHandle}
          onMouseDown={startResize}
          data-testid="panel-resize-handle"
          title="Drag to resize the panel"
        >
          <div style={styles.resizeGrip} />
        </div>
        {/* Dashboard RIGHT (resizable) */}
        <div style={{ ...styles.dashCol, width: panelWidth }}>
          <Dashboard applyLocation={applyLocation} onEpwFile={handleEpwFile} onEpwClear={handleEpwClear} viability={viability} kpis={kpis} />
        </div>
      </div>

      {/* KPI ribbon — full width */}
      <KPIRibbon
        kpis={kpis}
        kb={kb}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        height={expandedRibbon}
        selections={selections}
        weather={weather}
        geo={geo}
        scenarios={scenarios}
        onDeleteScenario={deleteScenario}
        onLoadScenario={handleLoadScenario}
        onRenameScenario={renameScenario}
      />

      {/* AI Recommendation Assistant — floating */}
      <AIAssistant />

      {/* Energy operational-profile floating popout (opened from Energy resource cards) */}
      <EnergyProfilePanel />

      {/* Auth: top-right account control + modals */}
      <div style={{ position: "fixed", top: 16, right: 18, zIndex: 3200 }}>
        <AuthWidget />
      </div>
      <AuthModal />
      <ProfilePanel />
      <AdminPanel />

      {/* First-run Beta Terms of Service gate — blurs the app until accepted (localStorage-versioned) */}
      <WelcomeGate />

      {/* Quick Tour — guided onboarding walkthrough */}
      <QuickTour open={tourOpen} onClose={closeTour} onLoadSample={onUseSample} modelStatus={model.status} />

      {/* §12 — Scenario saved: slate-wipe or continue */}
      {showNewScenario && (
        <div style={styles.modalScrim} data-testid="new-scenario-modal">
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Scenario saved</div>
            <div style={styles.modalBody}>Your design is stored as a card in the ribbon below. Start a fresh design scenario on the same building, or keep editing this one?</div>
            <div style={styles.modalActions}>
              <button
                style={styles.modalGhost}
                data-testid="scenario-continue-btn"
                onClick={() => setShowNewScenario(false)}
              >
                Keep editing
              </button>
              <button
                style={styles.modalPrimary}
                data-testid="scenario-new-btn"
                onClick={() => { startNewScenario(); setShowNewScenario(false); setExpanded(false); showToast("New scenario. Site and building kept, design reset"); }}
              >
                Start new design
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest → account conversion nudge (fires once, after the first saved design; A/B copy) */}
      {signupNudgeOpen && (
        <div style={styles.modalScrim} data-testid="signup-nudge-modal">
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>{NUDGE_COPY[nudgeVariant].title}</div>
            <div style={styles.modalBody}>{NUDGE_COPY[nudgeVariant].body}</div>
            <div style={styles.modalActions}>
              <button
                style={styles.modalGhost}
                data-testid="signup-nudge-dismiss-btn"
                onClick={() => { setSignupNudgeOpen(false); setShowNewScenario(true); trackEvent("signup_nudge_dismiss", { variant: nudgeVariant }); }}
              >
                Maybe later
              </button>
              <button
                style={styles.modalPrimary}
                data-testid="signup-nudge-accept-btn"
                onClick={() => { setSignupNudgeOpen(false); useAuth.getState().openModal("signup"); trackEvent("signup_nudge_accept", { variant: nudgeVariant }); }}
              >
                {NUDGE_COPY[nudgeVariant].cta}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P5 — Export Project scope modal */}
      {exportModalOpen && (
        <div style={styles.modalScrim} data-testid="export-scope-modal">
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Include scenarios and their comparisons in the export?</div>
            <div style={styles.modalBody}>Your saved scenario cards and their comparison metrics can be bundled into the .rcengine file{scenarios?.length ? ` (${scenarios.length} saved)` : ""}. Or export just the current design + model.</div>
            <div style={styles.modalActions}>
              <button style={styles.modalGhost} data-testid="export-without-scenarios-btn" onClick={() => exportProject(false)}>
                Current design only
              </button>
              <button style={styles.modalPrimary} data-testid="export-with-scenarios-btn" onClick={() => exportProject(true)}>
                Include scenarios
              </button>
            </div>
            <button style={styles.modalClose} data-testid="export-cancel-btn" onClick={() => setExportModalOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      <ReportDetailsModal open={reportModalOpen} onClose={() => setReportModalOpen(false)} />

      {/* P4 — Autosave opt-in prompt (fires after 10 min of active session) */}
      {autosavePromptOpen && (
        <div style={styles.modalScrim} data-testid="autosave-prompt-modal">
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Would you like to turn on Autosave?</div>
            <div style={styles.modalBody}>We&apos;ll silently back up your whole project (every scenario and the attached model) to the cloud every 15 minutes, so you never lose work.</div>
            <div style={styles.modalActions}>
              <button style={styles.modalGhost} data-testid="autosave-decline-btn" onClick={() => setAutosavePromptOpen(false)}>
                Not now
              </button>
              <button style={styles.modalPrimary} data-testid="autosave-accept-btn" onClick={acceptAutosavePrompt}>
                Turn on Autosave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* P4 — Define the project (folder) name before autosave can run */}
      {nameProjectOpen && (
        <div style={styles.modalScrim} data-testid="name-project-modal">
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Name your project</div>
            <div style={styles.modalBody}>Give this project a name. All its scenarios and the model autosave under it.</div>
            <input
              autoFocus
              style={styles.projectNameInput}
              value={projectNameDraft}
              onChange={(e) => setProjectNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmProjectName(); }}
              placeholder="e.g. Riverside Rooftop Farm"
              data-testid="project-name-input"
            />
            <div style={styles.modalActions}>
              <button style={styles.modalGhost} data-testid="name-project-cancel-btn" onClick={() => setNameProjectOpen(false)}>
                Cancel
              </button>
              <button style={styles.modalPrimary} data-testid="name-project-confirm-btn" onClick={confirmProjectName} disabled={!projectNameDraft.trim()}>
                Save & enable Autosave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Local session cache — offer to resume the last design after a refresh/close */}
      {resumeData && (
        <div style={styles.resumeBanner} data-testid="resume-session-banner">
          <div style={styles.resumeDot} />
          <div style={styles.resumeText}>
            <div style={styles.resumeTitle}>Resume your last session?</div>
            <div style={styles.resumeSub}>{sessionSummary(resumeData)}</div>
          </div>
          <button style={styles.resumeGhost} data-testid="resume-dismiss-btn" onClick={dismissResume}>
            Start fresh
          </button>
          <button style={styles.resumePrimary} data-testid="resume-confirm-btn" onClick={resumeLastSession}>
            Resume
          </button>
        </div>
      )}

      {/* Transient glass toast (scenario restore, guidance) */}
      {toast && (
        <div style={styles.toast} data-testid="app-toast">
          <span style={styles.toastDot} />
          {toast}
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { height: "100vh", width: "100vw", overflow: "hidden", display: "flex", flexDirection: "column", background: colors.bg },
  topRow: { flex: 1, minHeight: 0, display: "flex", flexDirection: "row" },
  viewportCol: { flex: 1, position: "relative", minWidth: 0 },
  tourBtn: { display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.7)", background: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 4px 14px rgba(0,0,0,0.1)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 600, color: colors.textPrimary, cursor: "pointer" },
  viewToolbar: { position: "absolute", left: 16, zIndex: 25, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 },
  undoGroup: { display: "flex", alignItems: "center", gap: 4, padding: 4, borderRadius: 999, background: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.7)", boxShadow: "0 4px 14px rgba(0,0,0,0.1)" },
  iconBtn: { width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 999, border: "none", background: "transparent", color: colors.textPrimary },
  scopeToggle: { display: "flex", padding: 4, borderRadius: 999, background: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.7)", boxShadow: "0 4px 14px rgba(0,0,0,0.1)", gap: 2 },
  scopeBtn: { padding: "6px 14px", borderRadius: 999, border: "none", background: "transparent", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 600, color: colors.textSecondary, cursor: "pointer", transition: "background 0.18s, color 0.18s" },
  scopeBtnOn: { background: colors.brand, color: "#fff", boxShadow: "0 2px 8px rgba(81,170,254,0.35)" },
  possibilitiesBtn: { display: "flex", alignItems: "center", gap: 8, padding: "8px 15px", borderRadius: 999, border: "none", background: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 4px 14px rgba(0,0,0,0.1)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 700, color: colors.textPrimary, cursor: "pointer", transition: "background 0.18s, color 0.18s" },
  possibilitiesDot: { width: 9, height: 9, borderRadius: 999, flexShrink: 0 },
  reportBtn: { padding: "9px 16px", borderRadius: 999, border: "none", background: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 4px 14px rgba(0,0,0,0.1)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 700, color: colors.brand, cursor: "pointer" },
  dockSaveBtn: { padding: "9px 16px", borderRadius: 999, border: "none", background: colors.brand, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 4px 14px rgba(81,170,254,0.32)", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 700, color: "#fff", cursor: "pointer" },
  dashCol: { flexShrink: 0, minHeight: 0 },
  resizeHandle: { flexShrink: 0, width: 8, cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", zIndex: 30 },
  resizeGrip: { width: 4, height: 46, borderRadius: 999, background: "rgba(0,0,0,0.16)" },
  toast: {
    position: "fixed",
    bottom: 158,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "12px 18px",
    borderRadius: 999,
    background: "rgba(20,22,26,0.9)",
    color: "#fff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: -0.1,
    boxShadow: "0 12px 40px rgba(0,0,0,0.28)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    zIndex: 60,
    animation: "bia-toast-in 0.3s ease",
  },
  toastDot: { width: 8, height: 8, borderRadius: 999, background: "linear-gradient(135deg, #A3CD40, #212E20)", flexShrink: 0 },
  resumeBanner: {
    position: "fixed",
    top: 18,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "12px 14px 12px 18px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(24px) saturate(180%)",
    WebkitBackdropFilter: "blur(24px) saturate(180%)",
    border: "1px solid rgba(255,255,255,0.7)",
    boxShadow: "0 16px 50px rgba(0,0,0,0.22)",
    zIndex: 90,
    maxWidth: "92vw",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
    animation: "bia-toast-in 0.3s ease",
  },
  resumeDot: { width: 9, height: 9, borderRadius: 999, background: colors.brand, flexShrink: 0, boxShadow: `0 0 0 4px ${colors.brand}22` },
  resumeText: { minWidth: 0 },
  resumeTitle: { fontSize: 13.5, fontWeight: 700, color: colors.textPrimary, letterSpacing: -0.2 },
  resumeSub: { fontSize: 11.5, fontWeight: 500, color: colors.textSecondary, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 260 },
  resumeGhost: { padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.14)", background: "transparent", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 600, color: colors.textSecondary, cursor: "pointer", flexShrink: 0 },
  resumePrimary: { padding: "8px 18px", borderRadius: 999, border: "none", background: colors.brand, fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 12.5, fontWeight: 700, color: "#fff", cursor: "pointer", flexShrink: 0, boxShadow: "0 4px 14px rgba(81,170,254,0.4)" },
  modalScrim: { position: "fixed", inset: 0, background: "rgba(16,18,22,0.44)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 80, animation: "bia-toast-in 0.24s ease" },
  modalCard: { width: 420, maxWidth: "90vw", padding: "26px 26px 22px", borderRadius: 22, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(30px) saturate(180%)", WebkitBackdropFilter: "blur(30px) saturate(180%)", border: "1px solid rgba(255,255,255,0.6)", boxShadow: "0 30px 80px rgba(0,0,0,0.3)", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif" },
  projectNameInput: { width: "100%", boxSizing: "border-box", margin: "14px 0 4px", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${colors.brand}`, outline: "none", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", fontSize: 14, color: colors.textPrimary, background: "#fff" },
  modalTitle: { fontSize: 20, fontWeight: 700, letterSpacing: -0.4, color: "#1d1d1f", marginBottom: 8 },
  modalBody: { fontSize: 14, lineHeight: 1.5, color: "#515154", marginBottom: 22 },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end" },
  modalGhost: { padding: "10px 18px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.12)", background: "#fff", fontSize: 13.5, fontWeight: 600, color: "#1d1d1f", cursor: "pointer" },
  modalPrimary: { padding: "10px 18px", borderRadius: 999, border: "none", background: "linear-gradient(135deg, #51aafe, #2E86D9)", fontSize: 13.5, fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: "0 8px 22px rgba(81,170,254,0.35)" },
  modalClose: { display: "block", margin: "14px auto 0", padding: "4px 10px", border: "none", background: "transparent", fontSize: 12.5, color: "#86868b", cursor: "pointer" },
};
